import { useState, useEffect, useRef, useCallback } from 'react';
import { REACT_APP_API_BASE } from '../../../config';

/**
 * Custom hook för att hämta alla distrikt i ett scenario
 * Kan användas av både klasskomponenter och funktionella komponenter
 * 
 * @param {string} type - Typ av distrikt ('livs' eller 'foodservice')
 * @param {string|null} scenarioId - Scenario ID från route params
 * @param {Function} fetchFn - Fetch function från withFetch HOC
 * @param {string|null} sortKey - Optional sort key
 * @param {string|null} sortDirection - Optional sort direction
 * @returns {Object} { activeDistricts, inActiveDistricts, scenarios, isLoading, reload }
 */
const useDistricts = (type, scenarioId, fetchFn, sortKey = null, sortDirection = null) => {
    const [state, setState] = useState({
        activeDistricts: null,
        inActiveDistricts: null,
        scenarios: null,
        isLoading: false
    });
    const isMountedRef = useRef(true);
    const fetchFnRef = useRef(fetchFn);
    const hasLoadedRef = useRef(false);

    // Uppdatera fetchFn ref när den ändras
    useEffect(() => {
        fetchFnRef.current = fetchFn;
    }, [fetchFn]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const isLoadingRef = useRef(false);
    const typeRef = useRef(type);
    const scenarioIdRef = useRef(scenarioId);

    // Uppdatera refs när värden ändras
    useEffect(() => {
        typeRef.current = type;
        scenarioIdRef.current = scenarioId;
    }, [type, scenarioId]);

    const fetchDistricts = useCallback(async (sortKeyParam = null, sortDirectionParam = null) => {
        const currentFetchFn = fetchFnRef.current;
        if (!currentFetchFn) return;

        // Förhindra dubbla anrop
        if (isLoadingRef.current) return;
        
        isLoadingRef.current = true;
        const currentType = typeRef.current;
        const currentScenarioId = scenarioIdRef.current;

        setState(prev => ({ ...prev, isLoading: true }));

        let baseURL = `${REACT_APP_API_BASE}/salesdistrict/${currentType}${currentScenarioId ? `/${currentScenarioId}` : ''}`;
        if (sortKeyParam) {
            baseURL = `${baseURL}?orderby=${sortKeyParam}&orderbydirection=${sortDirectionParam || 'asc'}`;
        }

        try {
            const res = await currentFetchFn(baseURL);
            const { scenarios, active, inActive } = await res.json();
            
            if (isMountedRef.current) {
                setState({
                    scenarios,
                    activeDistricts: active,
                    inActiveDistricts: inActive,
                    isLoading: false
                });
                hasLoadedRef.current = true;
                isLoadingRef.current = false;
            }
        } catch (error) {
            console.error('Failed to fetch districts', error);
            if (isMountedRef.current) {
                setState(prev => ({
                    ...prev,
                    isLoading: false
                }));
                isLoadingRef.current = false;
            }
        }
    }, []); // Tom dependency array - använder refs för värden

    useEffect(() => {
        // Reset hasLoaded när type eller scenarioId ändras
        hasLoadedRef.current = false;
        isLoadingRef.current = false;
        
        if (fetchFnRef.current && !hasLoadedRef.current) {
            fetchDistricts();
        }
    }, [type, scenarioId, fetchDistricts]);

    return {
        ...state,
        reload: fetchDistricts
    };
};

export default useDistricts;

